import { useState } from "react";
import Icon from "../../../../components/AppIcon";
import Button from "../../../../components/ui/Button";
import { useAuth } from "../../../../context/AuthContext";
import axiosInstance from "../../../../utils/axiosInstance";
import DispatchConfirmModal from "../modals/Dispatchconfirmmodal";
import { canAccess } from "../../../../utils/permissions";

/* ---------------------------------------
   STATUS → STEP INDEX
---------------------------------------- */
const mapApiStatusToStep = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return 0;
    case "on_the_way":
      return 1;
    case "arrived":
      return 2;
    case "sample_collected":
      return 3;
    case "enroute_to_hospital":
      return 4;
    case "completed":
      return 5;
    default:
      return 0;
  }
};

const OrderSidebar = ({
  order,
  onUpdateOrder,
  balance,
  onAddPayment,
  onReschedule,
  allResultsDone = false,
  pendingCount = 0,
  totalCount = 0,
}) => {
  const { user: currentUser } = useAuth();

  const [showDispatchModal, setShowDispatchModal] = useState(false);

  /* ── BASIC FLAGS ── */
  const normalizedStatus = String(order?.status || "").toLowerCase();
  const isCancelled =
    normalizedStatus === "cancelled" || normalizedStatus === "canceled";

  /* ── STATUS BADGE COLORS ── */
  const getStatusColor = (status) => {
    const map = {
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      on_the_way: "bg-black-100 text-black-800 border-black-200",
      arrived: "bg-indigo-100 text-indigo-800 border-indigo-200",
      sample_collected: "bg-purple-100 text-purple-800 border-purple-200",
      enroute_to_hospital: "bg-cyan-100 text-cyan-800 border-cyan-200",
      completed: "bg-green-100 text-green-800 border-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
      canceled: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      map[String(status || "").toLowerCase()] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  /* ── PAYMENT STATUS COLOR ── */
  const getPaymentStatusColor = (bal) => {
    const total = order?.finalAmount || order?.totalAmount || 0;
    if (Number(bal || 0) <= 0)
      return "bg-green-100 text-green-800 border-green-200";
    if (Number(bal || 0) < Number(total || 0))
      return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-red-100 text-red-800 border-red-200";
  };

  /* ── FORMAT STATUS TEXT ── */
  const formatStatus = (status) => {
    if (!status) return "—";
    return String(status)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  /* ── ORDER PROGRESS STEPS ── */
  const steps = [
    { status: "pending", label: "Order Placed", icon: "ShoppingCart" },
    { status: "on_the_way", label: "On The Way", icon: "RefreshCw" },
    { status: "arrived", label: "Arrived", icon: "MapPin" },
    { status: "sample_collected", label: "Sample Collected", icon: "Droplet" },
    { status: "enroute_to_hospital", label: "To Hospital", icon: "Truck" },
    { status: "completed", label: "Completed", icon: "Check" },
  ];

  const currentStep = mapApiStatusToStep(order?.status);

  /* ── DISPATCH CONDITIONS ── */
  const paymentCleared = Number(balance || 0) <= 0;

  const canDispatch =
    !isCancelled &&
    !order?.reportReady &&
    paymentCleared &&
    canAccess(currentUser, "canDispatch");

  const dispatchDisabledReason = (() => {
    if (isCancelled) return "Order is cancelled";
    if (order?.reportReady) return "Already dispatched / report ready";
    if (!paymentCleared) return "Clear payment balance first";
    return "";
  })();

  /* ── CANCEL ORDER ── */
  const handleCancelOrder = async (orderId) => {
    try {
      if (!orderId) return;
      if (isCancelled) {
        alert("This order is already cancelled.");
        return;
      }

      const ok = window.confirm(
        `Are you sure you want to cancel this order?\nOrder: ORD-${orderId}`
      );
      if (!ok) return;

      await axiosInstance.put(
        `${import.meta.env.VITE_API_BASE_URL}/api/orders/${orderId}/cancel`,
        { cancelledBy: currentUser?.name },
        { withCredentials: true }
      );

      alert(`Order ORD-${orderId} cancelled successfully.`);
      await onUpdateOrder();
    } catch (err) {
      console.error("Cancel error:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to cancel order. Please try again."
      );
    }
  };

  /* ── DISPATCH CONFIRMED ── */
  const handleDispatchConfirmed = async () => {
    try {
      await onUpdateOrder({ reportReady: true, status: "completed" });
      setShowDispatchModal(false);
    } catch (e) {
      console.error(e);
      alert("Failed to dispatch report. Please try again.");
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* ── ORDER STATUS ── */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Activity" size={16} className="text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Order Status</h3>
          </div>

          <div className="space-y-3">
            {/* Current Status */}
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Current Status</p>
              <div
                className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-medium ${getStatusColor(
                  order?.status
                )}`}
              >
                <Icon name="Circle" size={8} className="mr-1.5" />
                {formatStatus(order?.status)}
              </div>
            </div>

            {/* Payment Status */}
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Payment Status</p>
              <div
                className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-medium ${getPaymentStatusColor(
                  balance
                )}`}
              >
                <Icon
                  name={paymentCleared ? "CheckCircle" : "AlertCircle"}
                  size={12}
                  className="mr-1.5"
                />
                {paymentCleared
                  ? "Paid in Full"
                  : Number(balance || 0) <
                    Number(order?.finalAmount || order?.totalAmount || 0)
                  ? "Partially Paid"
                  : "Payment Due"}
              </div>
            </div>

            {/* Report Status */}
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Report Status</p>
              <div
                className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-medium ${
                  order?.reportReady
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-orange-50 text-orange-700 border-orange-200"
                }`}
              >
                <Icon
                  name={order?.reportReady ? "FileCheck" : "FileText"}
                  size={12}
                  className="mr-1.5"
                />
                {order?.reportReady ? "Report Ready" : "Report Pending"}
              </div>
            </div>

            {/* Dispatch / Regenerate button */}
            {canAccess(currentUser, "canDispatch") && (
              <div>
                {order?.reportReady ? (
                  <Button
                    onClick={() =>
                      onUpdateOrder({ reportReady: false, regenerateReport: true })
                    }
                    className="w-full text-xs h-8 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <Icon name="RefreshCcw" size={12} className="mr-1.5" />
                    Regenerate Report
                  </Button>
                ) : (
                  <Button
                    onClick={() => canDispatch && setShowDispatchModal(true)}
                    disabled={!canDispatch}
                    className={`w-full text-xs h-8 ${
                      canDispatch
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    <Icon name="Send" size={12} className="mr-1.5" />
                    Mark Report Dispatched
                  </Button>
                )}

                {!canDispatch && !order?.reportReady && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    {dispatchDisabledReason}
                  </p>
                )}
              </div>
            )}

            {/* Balance Due */}
            {Number(balance || 0) > 0 && !isCancelled && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-600">Balance Due</p>
                  <p className="text-sm font-bold text-red-600">
                    ₹{Number(balance || 0).toLocaleString("en-IN")}
                  </p>
                </div>
                <Button onClick={onAddPayment} className="w-full text-white text-xs h-8">
                  <Icon name="CreditCard" size={12} className="mr-1.5" />
                  Record Payment
                </Button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-5">
              <Button
                onClick={() =>
                  canAccess(currentUser, "canReschedule") && !isCancelled && onReschedule?.()
                }
                disabled={
                  !canAccess(currentUser, "canReschedule") ||
                  isCancelled ||
                  order?.status === "completed" ||
                  order?.status === "accepted"
                }
                variant="warning"
                className={`w-full text-xs py-2 ${
                  !canAccess(currentUser, "canReschedule") || isCancelled
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
              >
                <Icon name="CalendarClock" size={14} className="mr-1.5" />
                Reschedule Order
              </Button>

              <Button
                onClick={() =>
                  canAccess(currentUser, "canCancel") && handleCancelOrder(order?.id)
                }
                disabled={
                  !canAccess(currentUser, "canCancel") ||
                  isCancelled ||
                  order?.status === "completed" ||
                  order?.status === "accepted"
                }
                variant="danger"
                className={`w-full text-xs py-2 ${
                  !canAccess(currentUser, "canCancel") || isCancelled
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
              >
                <Icon name="XCircle" size={14} className="mr-1.5" />
                {isCancelled ? "Cancelled" : "Cancel Order"}
              </Button>
            </div>

            {isCancelled && (
              <p className="text-[11px] text-red-600 mt-1">
                This order is cancelled. Actions are disabled.
              </p>
            )}
          </div>
        </div>

        {/* ── VENDOR PROGRESS ── */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" size={16} className="text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Vendor Progress</h3>
          </div>

          {isCancelled ? (
            <div className="text-xs flex items-center gap-2 p-3 rounded-md border bg-red-50 border-red-200 text-red-700">
              <Icon name="Ban" size={14} />
              Order Cancelled — progress tracking stopped.
            </div>
          ) : (
            <div className="space-y-2 text-xs">
              {steps.map((step, index) => {
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                  <div key={step.status} className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? "bg-green-100 border border-green-300"
                          : isActive
                          ? "bg-black-100 border border-black-300"
                          : "bg-gray-100 border border-gray-300"
                      }`}
                    >
                      <Icon
                        name={step.icon}
                        size={12}
                        className={
                          isCompleted
                            ? "text-green-600"
                            : isActive
                            ? "text-black-600"
                            : "text-gray-400"
                        }
                      />
                    </div>
                    <span
                      className={
                        isCompleted
                          ? "text-green-700 font-medium"
                          : isActive
                          ? "text-black-700 font-medium"
                          : "text-gray-500"
                      }
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ✅ DISPATCH CONFIRM MODAL */}
      <DispatchConfirmModal
        isOpen={showDispatchModal}
        onClose={() => setShowDispatchModal(false)}
        onConfirm={handleDispatchConfirmed}
        orderId={order?.id}
        order={order}
        pendingCount={pendingCount}
        totalCount={totalCount}
      />
    </>
  );
};

export default OrderSidebar;